import { useCallback, useState, lazy, Suspense, useMemo } from "react";
import { toast, useToastRegister } from "./components/Toast";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { GradientGallery } from "./components/GradientGallery";
import { ScrollToTop } from "./components/ScrollToTop";
import { GitHubCorner } from "./components/GitHubCorner";
import { ThemeToggle } from "./components/ThemeToggle";
import { SplashScreen } from "./components/SplashScreen";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { PrivacyPolicy } from "./components/PrivacyPolicy";
import { AnimationStyles } from "./components/AnimationStyles";

// Lazy load the modal - it's not needed until user clicks a gradient
const GradientDetail = lazy(() =>
  import("./components/GradientDetail").then((m) => ({
    default: m.GradientDetail,
  })),
);
import { useAppState } from "./hooks/useAppState";
import { useKeyboard } from "./hooks/useKeyboard";
import { gradients } from "./data/gradients";
import {
  decodeGradient,
  gradientToCSS,
  encodeGradient,
  parseGradientCSS,
} from "./lib/gradient-url";
import type { GradientPreset } from "./types";

// Pre-compute encoded gradients for name lookup
const encodedToNameMap = new Map<string, string>();
gradients.forEach((g) => {
  const def = parseGradientCSS(g.gradient);
  if (def) {
    encodedToNameMap.set(encodeGradient(def), g.name);
  }
});

// Check if splash was shown within the last 24 hours
const SPLASH_STORAGE_KEY = "goodgradients-splash-seen";
const SPLASH_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

function shouldShowSplash(): boolean {
  try {
    const lastSeen = localStorage.getItem(SPLASH_STORAGE_KEY);
    if (!lastSeen) return true;
    const timestamp = parseInt(lastSeen, 10);
    if (isNaN(timestamp)) return true;
    return Date.now() - timestamp > SPLASH_EXPIRY_MS;
  } catch {
    return true; // Show splash if localStorage unavailable
  }
}

function markSplashSeen(): void {
  try {
    localStorage.setItem(SPLASH_STORAGE_KEY, Date.now().toString());
  } catch {
    // Ignore localStorage errors
  }
}

/*
  Routing sits in its own component so that the early return happens somewhere
  with no hooks in it. Previously App returned <PrivacyPolicy /> before calling
  any of its twelve hooks, which means the hook count differed between paths.
  It has never crashed only because the path is read from location at render
  and cannot change without a full reload; the first piece of client-side
  navigation would have white-screened the app with "rendered fewer hooks than
  expected". Keeping the conditional above the hooks removes the trap rather
  than relying on that.
*/
export default function App() {
  const path = window.location.pathname;
  if (path === "/privacy" || path === "/privacy.html") {
    return <PrivacyPolicy />;
  }
  return <Gallery />;
}

function Gallery() {
  const [showSplash, setShowSplash] = useState(shouldShowSplash);
  const { state, favorites, actions } = useAppState();

  // Register toast function
  useToastRegister();

  // Set up keyboard shortcuts
  useKeyboard({
    onEscape: () => {
      if (state.selectedGradient) {
        actions.selectGradient(null);
      }
    },
    onCopy: async () => {
      if (state.selectedGradient) {
        const gradientDef = decodeGradient(state.selectedGradient);
        if (gradientDef) {
          await navigator.clipboard.writeText(gradientToCSS(gradientDef));
          toast.success("Copied to clipboard");
        }
      }
    },
    onFavorite: () => {
      if (state.selectedGradient) {
        actions.toggleFavorite(state.selectedGradient);
      }
    },
    onToggleAnimation: () => {
      if (state.selectedGradient) {
        actions.toggleAnimating();
      }
    },
  });

  // Get the gradient name from the encoded gradient
  const selectedGradientName = useMemo(() => {
    if (!state.selectedGradient) return null;
    return encodedToNameMap.get(state.selectedGradient) ?? null;
  }, [state.selectedGradient]);

  // Handle gradient selection from gallery
  const handleSelectGradient = useCallback(
    (gradient: GradientPreset) => {
      actions.selectPreset(gradient);
    },
    [actions],
  );

  // Handle share - returns the shareable URL
  const handleShare = useCallback(() => {
    return actions.getShareURL();
  }, [actions]);

  // Toggle favorites filter - clear other filters when entering favorites mode
  const handleToggleFavorites = useCallback(() => {
    if (state.category === "Favorites") {
      // Leaving favorites - just switch to All, keep current filters
      actions.setCategory("All");
    } else {
      // Entering favorites - clear filters first, then switch
      actions.clearFilters();
      actions.setCategory("Favorites");
    }
  }, [actions, state.category]);

  // Get selected gradient definition (decoded from URL-encoded string)
  const selectedGradientDef = state.selectedGradient
    ? decodeGradient(state.selectedGradient)
    : null;

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Global animation keyframes - inject once to avoid interruption on re-renders */}
      <AnimationStyles />
      {showSplash && (
        <SplashScreen
          onComplete={() => {
            markSplashSeen();
            setShowSplash(false);
          }}
          minDuration={1200}
        />
      )}
      <Header
        colors={state.colors}
        tags={state.tags}
        gradientType={state.gradientType}
        previewMode={state.previewMode}
        colorFormat={state.colorFormat}
        selectedAnimationId={state.selectedAnimationId}
        animationSpeed={state.animationSpeed}
        showFavoritesOnly={state.category === "Favorites"}
        onColorsChange={actions.setColors}
        onToggleColor={actions.toggleColor}
        searchQuery={state.searchQuery}
        onSearchChange={actions.setSearchQuery}
        onToggleTag={actions.toggleTag}
        onGradientTypeChange={actions.setGradientType}
        onPreviewModeChange={actions.setPreviewMode}
        onColorFormatChange={actions.setColorFormat}
        onAnimationChange={actions.selectAnimation}
        onAnimationSpeedChange={actions.setAnimationSpeed}
        onToggleFavorites={handleToggleFavorites}
        onClearFilters={actions.clearFilters}
        hasActiveFilters={actions.hasActiveFilters()}
      />

      {/*
        The document's only h1. The wordmark cannot be it: the header renders
        at two breakpoints, so marking it up as a heading produces two. The
        splash screen used to carry an h1 and then unmounted, leaving the page
        with no level-one heading and its first heading at level 2.
      */}
      <h1 className="sr-only">GoodGradients</h1>

      <main className="max-w-[1400px] mx-auto px-6 py-12">
        <ErrorBoundary>
          <GradientGallery
            category={state.category}
            searchQuery={state.searchQuery}
            colors={state.colors}
            tags={state.tags}
            gradientType={state.gradientType}
            previewMode={state.previewMode}
            colorFormat={state.colorFormat}
            selectedAnimationId={state.selectedAnimationId}
            animationSpeed={state.animationSpeed}
            favorites={favorites}
            onSelectGradient={handleSelectGradient}
            onToggleFavorite={actions.toggleFavorite}
            isFavorite={actions.isFavorite}
            onClearFilters={actions.clearFilters}
          />
        </ErrorBoundary>
      </main>

      <Footer />

      <ScrollToTop />
      <GitHubCorner />
      <ThemeToggle />

      {/* Gradient Detail Modal - lazy loaded */}
      {selectedGradientDef && (
        <ErrorBoundary>
          <Suspense fallback={null}>
            <GradientDetail
              gradientDef={selectedGradientDef}
              gradientName={selectedGradientName}
              encodedGradient={state.selectedGradient}
              isOpen={!!selectedGradientDef}
              onClose={actions.closeModal}
              selectedAnimationId={state.selectedAnimationId}
              animationSpeed={state.animationSpeed}
              isAnimating={state.isAnimating}
              isFavorite={
                state.selectedGradient
                  ? actions.isFavorite(state.selectedGradient)
                  : false
              }
              colorFormat={state.colorFormat}
              previewMode={state.previewMode}
              skipAnimation={actions.shouldSkipModalAnimation()}
              onGradientChange={actions.updateGradient}
              onAnimationChange={actions.selectAnimation}
              onAnimationSpeedChange={actions.setAnimationSpeed}
              onToggleAnimating={actions.toggleAnimating}
              onToggleFavorite={() =>
                state.selectedGradient &&
                actions.toggleFavorite(state.selectedGradient)
              }
              onColorFormatChange={actions.setColorFormat}
              onPreviewModeChange={actions.setPreviewMode}
              onShare={handleShare}
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </div>
  );
}
